import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { authService } from '../services/authService';
import { tasksService } from '../services/tasksService';
import { mapEstados, calcularKpisMantenimientos } from '../utils/mantenimientosKpis';

// Dashboard propio de atención al cliente — centrado en comunicación de
// contratos (renovaciones/firmas pendientes) y facturación pendiente, no en
// operativa de campo ni en cifras de facturación/MRR (eso es Hoy/Operativo/
// Comercial de DashboardView.js, que este rol no ve). Reutiliza
// calcularKpisMantenimientos — misma fuente de verdad que TabAnalitica en
// MantenimientosView.js — para no divergir en los conteos.

function diasDesde(dateStr) {
  if (!dateStr) return null;
  const hoy = new Date(); hoy.setHours(0, 0, 0, 0);
  const d = new Date(dateStr + 'T00:00:00');
  if (isNaN(d.getTime())) return null;
  return Math.max(0, Math.round((hoy - d) / 86400000));
}

function mediaDias(lista) {
  const dias = lista.map(m => diasDesde(m.updatedAt)).filter(d => d !== null);
  if (dias.length === 0) return null;
  return Math.round(dias.reduce((s, d) => s + d, 0) / dias.length);
}

const StatCard = ({ icon, label, value, sub, color, onClick }) => (
  <button
    onClick={onClick}
    className={`w-full text-left bg-white rounded-2xl border shadow-sm hover:shadow-md transition-all p-5 ${onClick ? 'cursor-pointer hover:scale-[1.01]' : 'cursor-default'}`}
  >
    <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${color}`}>
      {icon}
    </div>
    <p className="text-2xl font-bold text-gray-900">{value}</p>
    <p className="text-sm font-medium text-gray-700 mt-0.5">{label}</p>
    {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
  </button>
);

const DashboardAtencionCliente = ({ onNavigate }) => {
  const { user } = useAuth();
  const [kpis, setKpis] = useState(null);
  const [ariston, setAriston] = useState(null);
  const [tareas, setTareas] = useState([]);
  const [tareasDate, setTareasDate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const cargar = async () => {
      setLoading(true); setError('');
      try {
        const { db } = await import('../config/firebase');
        const { collection, getDocs, query, where } = await import('firebase/firestore');

        const [mantsRes, aristonSnap, tareasRes] = await Promise.all([
          fetch('/.netlify/functions/getMantenimientos', { headers: { ...authService.authHeader() } }).then(r => r.json()),
          getDocs(query(collection(db, 'ordenes'), where('estado', '==', 'garantia_ariston'))),
          tasksService.getTasksForUser('atencion_cliente'),
        ]);

        if (mantsRes?.success) {
          const data = mapEstados(mantsRes.mantenimientos || []);
          setKpis(calcularKpisMantenimientos(data));
        }

        const aristonOrdenes = aristonSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        const diasAriston = aristonOrdenes
          .map(o => diasDesde(o.updatedAt || o.fechaProgramada))
          .filter(d => d !== null);
        setAriston({
          count: aristonOrdenes.length,
          media: diasAriston.length ? Math.round(diasAriston.reduce((s, d) => s + d, 0) / diasAriston.length) : null,
        });

        if (tareasRes?.success) {
          setTareas(tareasRes.tasks || []);
          setTareasDate(tareasRes.date || null);
        }
      } catch (e) {
        console.error('[DashboardAtencionCliente]', e);
        setError('No se pudieron cargar los datos del dashboard.');
      } finally {
        setLoading(false);
      }
    };
    cargar();
  }, []);

  const skeleton = (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      {[1, 2, 3, 4].map(i => (
        <div key={i} className="bg-white rounded-2xl border shadow-sm p-5 animate-pulse">
          <div className="w-10 h-10 bg-gray-100 rounded-xl mb-3" />
          <div className="h-6 bg-gray-100 rounded w-1/3 mb-2" />
          <div className="h-3 bg-gray-100 rounded w-2/3" />
        </div>
      ))}
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl shadow-lg border p-5">
        <h2 className="text-xl font-bold text-gray-800">
          Buenos días, {user?.name?.split(' ')[0]} 👋
        </h2>
        <p className="text-gray-400 text-sm mt-0.5">
          {new Date().toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' })}
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-2 text-sm">{error}</div>
      )}

      {loading ? skeleton : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              icon={<span className="text-lg">✍️</span>}
              color="bg-purple-50"
              label="Pendiente firmar"
              value={kpis?.pendienteFirmar ?? 0}
              sub="enviados sin firmar"
              onClick={() => onNavigate('mantenimientos')}
            />
            <StatCard
              icon={<span className="text-lg">🔄</span>}
              color="bg-orange-50"
              label="Pendiente renovar"
              value={kpis?.pendRenovar ?? 0}
              sub={kpis?.pendRenovarLista?.length ? `media ${mediaDias(kpis.pendRenovarLista) ?? '—'} días esperando` : 'agendar próxima visita'}
              onClick={() => onNavigate('mantenimientos')}
            />
            <StatCard
              icon={<span className="text-lg">⏳</span>}
              color="bg-amber-50"
              label="Vence pronto"
              value={kpis?.vencePronto ?? 0}
              sub="≤30 días — avisar antes"
              onClick={() => onNavigate('mantenimientos')}
            />
            <StatCard
              icon={<span className="text-lg">↩️</span>}
              color="bg-red-50"
              label="SEPA devueltos"
              value={kpis?.sepaDevueltos ?? 0}
              sub="corregir IBAN"
              onClick={() => onNavigate('mantenimientos')}
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Preliquidación Ariston — órdenes cerradas por facturar */}
            <div className="bg-white rounded-2xl border shadow-sm overflow-hidden">
              <div className="bg-gray-50 border-b px-5 py-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full bg-gray-400" />
                  <h3 className="font-bold text-sm text-gray-700">Preliquidación Ariston — por facturar</h3>
                </div>
                <button
                  onClick={() => onNavigate('v2-ordenes')}
                  className="text-xs text-gray-500 hover:text-gray-700 font-medium"
                >
                  Ver órdenes →
                </button>
              </div>
              <div className="px-5 py-6 flex items-center gap-4">
                <p className="text-3xl font-bold text-gray-900">{ariston?.count ?? 0}</p>
                <div className="text-sm text-gray-500">
                  <p>orden{ariston?.count === 1 ? '' : 'es'} en garantía Ariston</p>
                  {ariston?.media !== null && ariston?.count > 0 && (
                    <p className="text-xs text-gray-400 mt-0.5">antigüedad media: {ariston.media} días</p>
                  )}
                </div>
              </div>
            </div>

            {/* Tareas del día */}
            <div className="bg-white rounded-2xl border shadow-sm overflow-hidden">
              <div className="bg-blue-50 border-b border-blue-100 px-5 py-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full bg-blue-400" />
                  <h3 className="font-bold text-sm text-blue-700">
                    Tareas pendientes{tareasDate && <span className="font-normal"> · {tareasDate}</span>}
                  </h3>
                </div>
                <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
                  {tareas.length}
                </span>
              </div>
              <div className="px-5 py-3 max-h-72 overflow-y-auto">
                {tareas.length === 0 ? (
                  <p className="text-sm text-gray-400 py-4 text-center">🎉 Sin tareas pendientes</p>
                ) : (
                  tareas.slice(0, 15).map(t => (
                    <div key={t.id} className="flex items-start gap-3 py-2.5 border-b border-gray-50 last:border-0">
                      <span className="text-base flex-shrink-0 mt-0.5">
                        {t.priority === 'high' ? '🔴' : t.priority === 'medium' ? '🟡' : '🟢'}
                      </span>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-800 truncate">
                          {t.workorderData?.fullReference || t.title}
                        </p>
                        <p className="text-xs text-gray-500 truncate">{t.description}</p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default DashboardAtencionCliente;
